/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Box, HStack, Text } from '@chakra-ui/react';
import { CQReactTable } from '@laxmimanogna/code-quick-components';
import { RebuttalIcon } from '../../../../constants/IconData';

import { createColumnHelper } from '@tanstack/react-table';
import {
  getAuditStatus,
  MY_AUDITS_ORDERING,
} from '../../../../constants/constants';
import useCQMediaQuery from '../../../../hooks/useCQMediaQuery';
import { format, formatDistanceStrict } from 'date-fns';

import '../style.css';
import { MyAuditContext } from '../../../../providers/MyAuditProvider';
import { renderActionComponent } from '../MyAuditsComponents';
import {
  defaultReactTableProp,
  paginationProps,
} from '../../../../utils/my_audits.utils';
import { useNavigate } from 'react-router-dom';
import { toTitleCase } from '../../../../utils/common.utils';

const columnHelper = createColumnHelper();

const MyRebuttalTable = () => {
  const myAuditContext = useContext(MyAuditContext);
  const navigate = useNavigate();
  const [ordering, setOrdering] = useState('');

  // media query
  const canScroll = useCQMediaQuery({
    lg: false,
    md: false,
    sm: true,
  });

  const { isUpdating, getAuditRebuttals, filterParams } = myAuditContext;

  useEffect(() => {
    let searching = !!(filterParams.searchText && filterParams.searchText.trim());
    getAuditRebuttals.loadAPI("My Audit", "My Rebuttal", searching);
  }, [filterParams, getAuditRebuttals.showOrdering]);

  useEffect(() => {
    if (MY_AUDITS_ORDERING.includes(ordering)) {
      getAuditRebuttals.setParams({ ordering });
    }
  }, [ordering]);

  const columns = useMemo(
    () => [
      columnHelper.accessor('upload_id', {
        header: () => 'Upload #',
        id: 'chart_id',
        cell: info => {
          return (
            <HStack
              justifyContent={'flex-start'}
              alignItems={'center'}
              spacing={5}
            >
              <RebuttalIcon style={{ height: 20, width: 20 }} />
              <Text>{info.renderValue()}</Text>
            </HStack>
          );
        },
      }),
      columnHelper.accessor(row => row.uploaded_date, {
        id: 'upload_date',
        header: () => <span>Chart Upload</span>,
        cell: info => {
          const cellValue = info.getValue();
          if (!cellValue) {
            return 'N/A';
          }

          return format(new Date(cellValue), 'MM/dd/yyyy');
        },
      }),
      //need to modify
      columnHelper.accessor(row => row.audited_date, {
        id: 'audited_date',
        header: () => <span>Audit Upload</span>,
        cell: info => {
          const cellValue = info.getValue();
          if (!cellValue) {
            return 'N/A';
          }

          return format(new Date(cellValue), 'MM/dd/yyyy');
        },
      }),

      columnHelper.accessor(p => p.specialties?.name, {
        header: () => 'Specialties',
        id: 'specialty__name',
        cell: info => {
          const specialties = info.row.original.specialties?.name;

          if (!specialties) {
            return 'N/A';
          }
          return specialties === 'ent' ? 'ENT' : toTitleCase(specialties);
        },
      }),
      columnHelper.accessor(p => p.assigned_qa?.first_name, {
        header: () => 'QA',
        id: 'qa',
        cell: info => {
          const cellValue = info.row.original.assigned_qa;
          if (!cellValue) {
            return 'N/A';
          }

          return `${cellValue.first_name} ${cellValue.last_name}`;
        },
      }),
      columnHelper.accessor('status', {
        header: () => 'Status',
        id: 'status',
        cell: info => <Text>{getAuditStatus(info.row.original?.status)}</Text>,
      }),

      columnHelper.accessor('updated_date', {
        header: () => 'Last Updated',
        id: 'updated_date',
        cell: info => {
          let updatedAt = info.getValue();

          updatedAt = updatedAt
            ? formatDistanceStrict(new Date(updatedAt), new Date(), {
              addSuffix: true,
            })
            : 'N/A';

          return (
            <Text>
              {updatedAt !== 'N/A' ? toTitleCase(updatedAt) : updatedAt}
            </Text>
          );
        },
      }),
      columnHelper.accessor(row => row, {
        id: 'actions',
        header: () => 'Actions',
        enableSorting: false,
        cell: info => {
          const obj = info.getValue();
          return renderActionComponent(obj, navigate, myAuditContext);
        },
      }),
    ],
    [myAuditContext.isAuditUploadListLoading]
  );

  function handlePreviousPage() {
    myAuditContext.getAuditRebuttals.loadPrevious("My Audit", "My Rebuttal");
  }

  function handleNextPage() {
    myAuditContext.getAuditRebuttals.loadNext("My Audit", "My Rebuttal");
  }

  function handleSizeChange(size) {
    myAuditContext.getAuditRebuttals.loadSize("My Audit", "My Rebuttal" ,size );
  }

  function handlePageChangeByNumber(pageNumber) {
    myAuditContext.getAuditRebuttals.loadPageByNumber("My Audit", "My Rebuttal", pageNumber);
  }

  return (
    <Box
      pointerEvents={isUpdating ? 'none' : 'inherit'}
      overflowX={canScroll ? 'scroll' : 'inherit'}
    >
      <CQReactTable
        manualSortBy={true}
        isLoading={myAuditContext.isAuditUploadListLoading}
        data={myAuditContext.auditRebuttalsList}
        columns={columns}
        {...defaultReactTableProp}
        {...paginationProps(getAuditRebuttals)}
        onPreviousPage={handlePreviousPage}
        onNextPage={handleNextPage}
        onSizeChange={handleSizeChange}
        onPageChangeByNumber={handlePageChangeByNumber}
        onAsc={header => {
          setOrdering(header);
        }}
        onDesc={header => {
          setOrdering(`-${header}`);
        }}
      />
    </Box>
  );
};
export default MyRebuttalTable;
